import { formatDistanceToNow } from "date-fns";
import { Fingerprint } from "lucide-react";

import { Badge } from "../base/badge";
import { cn } from "../../lib/utils";

/**
 * Last reported configuration CRC and what it corresponds to (OTO-170).
 *
 * Mirrors `configFingerprintSchema` on the backend. Kept as a local type rather than the generated
 * one because the search cards define their own narrow device shapes.
 */
export type ConfigFingerprintMatch =
  | { kind: "expected" }
  | { kind: "profile"; id: string; name: string }
  | { kind: "base_set"; firmwareVersion: string; variant?: string | null }
  | { kind: "ambiguous"; count: number }
  | { kind: "unmatched" }
  | { kind: "unreported" };

export interface ConfigFingerprint {
  crc?: string | null;
  observedAt?: string | Date | null;
  changedAt?: string | Date | null;
  match: ConfigFingerprintMatch;
}

type BadgeVariant = "ok" | "info" | "warning" | "secondary";

/**
 * How each verdict reads to an operator.
 *
 * `profile` / `base_set` are deliberately **info**, not warning: a device matching a configuration
 * it is not assigned means our expectation is wrong, not the device — the founding premise of the
 * candidate matching in configDrift/candidates.ts. Painting that amber would train operators to
 * ignore the colour that actually means "nobody knows what this device is running".
 */
export function describeConfigFingerprintMatch(match: ConfigFingerprintMatch): {
  label: string;
  variant: BadgeVariant;
  title: string;
} {
  switch (match.kind) {
    case "expected":
      return {
        label: "Standard",
        variant: "ok",
        title: "Reported CRC matches the configuration we expect for this device.",
      };
    case "profile":
      return {
        label: `Known: ${match.name}`,
        variant: "info",
        title: `Reported CRC matches the "${match.name}" profile, which is not assigned to this device.`,
      };
    case "base_set": {
      const name = match.variant
        ? `Firmware ${match.firmwareVersion} (${match.variant} batch)`
        : `Firmware ${match.firmwareVersion} defaults`;
      return {
        label: `Known: ${name}`,
        variant: "info",
        title: `Reported CRC matches ${name}, which is not what this device resolves against.`,
      };
    }
    case "ambiguous":
      // `count` is only ever >= 2 here — a single hit returns `profile`/`base_set` — so the
      // singular form would always have been wrong.
      return {
        label: `Known (${match.count} matches)`,
        variant: "info",
        title: `Reported CRC matches ${match.count} known configurations — open the device to compare them.`,
      };
    case "unmatched":
      return {
        label: "Non-standard",
        variant: "warning",
        title: "Reported CRC does not match any configuration we know about.",
      };
    case "unreported":
      return {
        label: "No CRC",
        variant: "secondary",
        title: "This device has never reported a configuration CRC.",
      };
  }
}

/**
 * The CRC itself, with its age.
 *
 * The age is NOT decoration. The Maxi telemetry sweep runs once a day (`37 4 * * *`), so a CRC can
 * legitimately be ~24h old — an operator who has just reconfigured a device must be able to see
 * that they are looking at yesterday's reading rather than the result of their change.
 */
export function ConfigFingerprintValue({
  fingerprint,
  className,
}: {
  fingerprint: ConfigFingerprint;
  className?: string;
}) {
  if (!fingerprint.crc) return null;

  const observedAt = fingerprint.observedAt ? new Date(fingerprint.observedAt) : null;
  const age =
    observedAt && !Number.isNaN(observedAt.getTime())
      ? formatDistanceToNow(observedAt, { addSuffix: true })
      : null;

  return (
    <span className={cn("flex items-center gap-1 text-muted-foreground", className)}>
      <Fingerprint className="h-3 w-3 shrink-0" />
      <span className="font-mono text-xs">{fingerprint.crc}</span>
      {age && <span className="text-xs">· {age}</span>}
    </span>
  );
}

export function ConfigFingerprintBadge({
  fingerprint,
  className,
}: {
  fingerprint: ConfigFingerprint;
  className?: string;
}) {
  const { label, variant, title } = describeConfigFingerprintMatch(fingerprint.match);
  return (
    <Badge variant={variant} className={cn("text-xs max-w-[14rem] truncate", className)} title={title}>
      {label}
    </Badge>
  );
}
